import {
  montantDepuisPart,
  partDepuisMontant,
  repartirAcompte,
  totauxAcompte,
  validerAcompte,
} from '@imp/shared/acompte';
import { adresseDeFacturation, controlesAvantEmission } from '@imp/shared/controle-emission';
import { formatDateFr } from '@imp/shared/dates';
import { messageErreur } from '@imp/shared/message-erreur';
import {
  messageMontantInvalide,
  reglementSaisi,
  remboursementSaisi,
} from '@imp/shared/saisie-montant';
import { controlerEmission } from '@imp/shared/suivi-facturation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, CheckCheck, Lock, Printer, Send, Trash2, Undo2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { formatEuro, formatTauxPct, listTauxTva, listUnites } from '@/modules/catalogue/api';
import { ChoixChantier } from '@/modules/chantiers/ChoixChantier';
import { listChantiers } from '@/modules/chantiers/api';
import { contactDisplayName, listAdressesClient, listContacts } from '@/modules/clients/api';
import {
  creerAvoir,
  emettreAcompte,
  emitFacture,
  FACTURE_STATUT_LABELS,
  getContexteChantier,
  getFacture,
  listReglements,
  markFactureEnvoyee,
  recordReglement,
  REGLEMENT_MODE_LABELS,
  setFactureChantier,
  supprimerReglement,
} from '@/modules/factures/api';

/** Une adresse en lignes d'enveloppe, sans les morceaux vides. */
function lignesAdresse(
  ligne1: string | null | undefined,
  ligne2: string | null | undefined,
  codePostal: string | null | undefined,
  ville: string | null | undefined,
): string[] {
  return [ligne1, ligne2, `${codePostal ?? ''} ${ville ?? ''}`]
    .map((x) => (x ?? '').trim())
    .filter((x) => x !== '');
}

/** Lit un nombre tapé à la française : « 30,5 » vaut 30.5. */
function nombre(v: string): number {
  return Number(v.replace(',', '.'));
}

export function FactureDetailPage() {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [confirmEmit, setConfirmEmit] = useState(false);
  // Émission : la totalité, ou un acompte. La PART fait foi ; les deux champs
  // (pourcentage et euros) l'affichent, celui qu'on tape garde sa frappe.
  const [emissionAcompte, setEmissionAcompte] = useState(false);
  // Les points d'attention du récapitulatif doivent avoir été vus avant
  // d'émettre. Remis à zéro dès que le choix change : la liste peut changer avec lui.
  const [vuAttention, setVuAttention] = useState(false);
  const [part, setPart] = useState(0.3);
  const [saisiePct, setSaisiePct] = useState<string | null>(null);
  const [saisieEur, setSaisieEur] = useState<string | null>(null);
  const [confirmAvoir, setConfirmAvoir] = useState(false);
  const [regMode, setRegMode] = useState('virement');
  const [regMontant, setRegMontant] = useState('');
  const [regDate, setRegDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [regRef, setRegRef] = useState('');
  const [regSuppr, setRegSuppr] = useState<string | null>(null);

  const factureQ = useQuery({
    queryKey: ['facture', id],
    queryFn: () => getFacture(id ?? ''),
    enabled: Boolean(id),
  });
  const contactsQ = useQuery({ queryKey: ['contacts'], queryFn: listContacts });
  const tvaQ = useQuery({ queryKey: ['taux_tva'], queryFn: listTauxTva });
  const uniteQ = useQuery({ queryKey: ['unites'], queryFn: listUnites });
  const reglementsQ = useQuery({
    queryKey: ['reglements', id],
    queryFn: () => listReglements(id ?? ''),
    enabled: Boolean(id),
  });
  const chantiersQ = useQuery({ queryKey: ['chantiers'], queryFn: listChantiers });
  // Les pièces du chantier, pour savoir si ce brouillon facturerait plus qu'il
  // ne reste. Lu seulement pour un brouillon rattaché : ailleurs, rien à juger.
  const chantierId = factureQ.data?.chantier_id ?? null;
  const contexteQ = useQuery({
    queryKey: ['contexte-chantier', chantierId],
    queryFn: () => getContexteChantier(chantierId ?? ''),
    enabled: chantierId !== null && factureQ.data?.statut === 'brouillon',
  });

  // Les adresses du client, pour montrer AVANT l'émission celle qui sera figée.
  // Lecture qui échoue = on ne peut pas vérifier : l'émission attend.
  const contactIdFacture = factureQ.data?.contact_id ?? null;
  const adressesQ = useQuery({
    queryKey: ['adresses-client', contactIdFacture],
    queryFn: () => listAdressesClient(contactIdFacture ?? ''),
    enabled: contactIdFacture !== null && factureQ.data?.statut === 'brouillon',
  });

  const emit = useMutation({
    mutationFn: () =>
      emissionAcompte ? emettreAcompte(id ?? '', part) : emitFacture(id ?? ''),
    onSuccess: () => {
      setConfirmEmit(false);
      setEmissionAcompte(false);
      void queryClient.invalidateQueries({ queryKey: ['facture', id] });
      void queryClient.invalidateQueries({ queryKey: ['factures'] });
    },
  });
  const avoirMut = useMutation({
    mutationFn: () => creerAvoir(id ?? ''),
    onSuccess: (avoirId) => {
      setConfirmAvoir(false);
      void queryClient.invalidateQueries({ queryKey: ['facture', id] });
      void queryClient.invalidateQueries({ queryKey: ['factures'] });
      void queryClient.invalidateQueries({ queryKey: ['alertes'] });
      navigate(`/factures/${avoirId}`);
    },
  });
  const envoyeeMut = useMutation({
    mutationFn: () => markFactureEnvoyee(id ?? ''),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['facture', id] });
      void queryClient.invalidateQueries({ queryKey: ['factures'] });
    },
  });
  const chantierMut = useMutation({
    mutationFn: (chantierId: string | null) => setFactureChantier(id ?? '', chantierId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['facture', id] });
    },
  });
  const reglementMut = useMutation({
    mutationFn: () =>
      recordReglement(id ?? '', {
        mode: regMode,
        // Le montant part tel que l'utilisateur l'a tapé ; c'est l'API qui le
        // lit et qui REFUSE ce qu'elle ne comprend pas. Un `|| 0` ici avait
        // enregistré trois règlements de 0,00 € sans rien dire.
        montant: regMontant,
        date_reglement: regDate,
        ...(regRef.trim() === '' ? {} : { reference: regRef.trim() }),
      }),
    onSuccess: () => {
      setRegMontant('');
      setRegRef('');
      void queryClient.invalidateQueries({ queryKey: ['facture', id] });
      void queryClient.invalidateQueries({ queryKey: ['reglements', id] });
      void queryClient.invalidateQueries({ queryKey: ['factures'] });
    },
  });
  const supprReglementMut = useMutation({
    mutationFn: (reglementId: string) => supprimerReglement(id ?? '', reglementId),
    onSuccess: () => {
      setRegSuppr(null);
      void queryClient.invalidateQueries({ queryKey: ['facture', id] });
      void queryClient.invalidateQueries({ queryKey: ['reglements', id] });
      void queryClient.invalidateQueries({ queryKey: ['factures'] });
    },
  });

  const f = factureQ.data;
  const contact = (contactsQ.data ?? []).find((c) => c.id === f?.contact_id);
  const tvaMap = new Map((tvaQ.data ?? []).map((t) => [t.id, t]));
  const uniteMap = new Map((uniteQ.data ?? []).map((u) => [u.id, u]));

  if (factureQ.isLoading) return <p className="text-muted-foreground">Chargement…</p>;
  if (factureQ.isError || !f) {
    return (
      <p className="text-destructive">
        Erreur : {factureQ.error ? messageErreur(factureQ.error) : 'facture introuvable'}
      </p>
    );
  }

  const brouillon = f.statut === 'brouillon';
  const avoir = f.kind === 'avoir';

  // LE GARDE-FOU. Convertir un devis en facture déduit les acomptes déjà émis
  // — mais au moment de la conversion, et à ce moment-là seulement. Un
  // brouillon fabriqué AVANT l'acompte garde le montant plein, et l'émettre
  // refacture l'acompte une seconde fois.
  // Ce que le CRM lit dans le champ, et ce qu'il a à redire. Calculé à chaque
  // frappe : le bouton reste fermé tant que le montant n'est pas lisible.
  //
  // Sur un AVOIR, le négatif est permis — un remboursement est de l'argent qui
  // sort, et l'avoir porte lui-même un TTC négatif.
  const montantLu = avoir ? remboursementSaisi(regMontant) : reglementSaisi(regMontant);
  const erreurMontant = messageMontantInvalide(regMontant, { negatifAutorise: avoir });

  const ctx = contexteQ.data;
  const verdict =
    brouillon && ctx && f.chantier_id !== null
      ? controlerEmission(
          {
            id: f.id,
            chantierId: f.chantier_id,
            contactId: f.contact_id,
            numero: f.numero,
            kind: f.kind,
            statut: f.statut,
            factureLieeId: f.facture_liee_id,
            montantHt: Number(f.montant_ht),
            montantTtc: Number(f.montant_ttc),
            montantPaye: Number(f.montant_paye),
            montantOrigineHt: null,
          },
          ctx.montantTotalHt,
          ctx.factures,
          ctx.devis,
        )
      : { risque: 'aucun' as const };
  // ----- Le récapitulatif avant émission ----------------------------------
  const adresseFigee = adressesQ.data ? adresseDeFacturation(adressesQ.data) : null;
  const controlesPrets = adressesQ.isSuccess && contactsQ.isSuccess;
  const points = controlesPrets
    ? controlesAvantEmission({
        client: contact
          ? { typePersonne: contact.type_personne, email: contact.email, siret: contact.siret }
          : null,
        adresse: adresseFigee,
        nbLignes: f.factures_lignes.length,
        montantTtc: Number(f.montant_ttc),
        depassementHt: verdict.risque === 'depassement' ? verdict.ecartHt : null,
        acompte: emissionAcompte,
      })
    : [];
  const bloquants = points.filter((p) => p.niveau === 'bloquant');
  const attentions = points.filter((p) => p.niveau === 'attention');
  const chantierFacture = (chantiersQ.data ?? []).find((c) => c.id === f.chantier_id);
  const delaiJours = f.delai_paiement_jours > 0 ? f.delai_paiement_jours : 30;
  const echeancePrevue = (() => {
    const d = new Date();
    d.setDate(d.getDate() + delaiJours);
    return d.toLocaleDateString('fr-FR');
  })();
  const emissionPossible =
    controlesPrets && bloquants.length === 0 && (attentions.length === 0 || vuAttention);

  const typePiece =
    avoir
      ? 'Avoir'
      : f.kind === 'acompte'
        ? "Facture d'acompte"
        : f.kind === 'finale'
          ? 'Facture de solde'
          : 'Facture';
  const annulee = f.statut === 'annulee_avoir';
  // Document lié : la facture qu'annule cet avoir, ou l'avoir qui annule cette facture.
  const lie = (avoir ? f.metadata?.avoir_de : f.metadata?.annulee_par) ?? null;
  const detailTva = f.detail_tva ?? [];
  const paye = Number(f.montant_paye);
  const reste = Number(f.montant_ttc) - paye;

  // Acompte : la part choisie, telle qu'elle s'affiche et telle qu'elle sera
  // facturée. Le récapitulatif montre la SOMME DES LIGNES, pas le nombre rond
  // tapé — un arrondi par taux peut déplacer un centime, et la facture dit vrai.
  const totalTtc = Number(f.montant_ttc);
  const valeurPct = saisiePct ?? String(Math.round(part * 10000) / 100).replace('.', ',');
  const valeurEur = saisieEur ?? montantDepuisPart(part, totalTtc).toFixed(2).replace('.', ',');
  const totauxAcomptePrevu = totauxAcompte(repartirAcompte(detailTva, part));
  const refusAcompte = validerAcompte(part, totalTtc);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button asChild variant="ghost" size="icon">
            <Link to="/factures" aria-label="Retour">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">
              {typePiece}{' '}
              {brouillon ? (
                '(brouillon)'
              ) : (
                <span className="font-mono font-normal">{f.numero}</span>
              )}
            </h1>
            <span
              className={`mt-0.5 inline-block rounded px-2 py-0.5 text-xs font-medium ${brouillon || annulee ? 'bg-muted text-muted-foreground' : avoir ? 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200' : 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300'}`}
            >
              {FACTURE_STATUT_LABELS[f.statut] ?? f.statut}
            </span>
          </div>
        </div>
        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
          {!brouillon ? (
            <Button asChild variant="outline">
              <Link to={`/factures/${f.id}/pdf`}>
                <Printer className="h-4 w-4" /> PDF
              </Link>
            </Button>
          ) : null}
          {f.statut === 'emise' ? (
            <Button
              variant="outline"
              disabled={envoyeeMut.isPending}
              onClick={() => {
                envoyeeMut.mutate();
              }}
            >
              <Send className="h-4 w-4" /> Marquer envoyée
            </Button>
          ) : null}
          {!brouillon && !avoir && !annulee && !confirmAvoir ? (
            <Button
              variant="outline"
              onClick={() => {
                setConfirmAvoir(true);
              }}
            >
              <Undo2 className="h-4 w-4" /> Faire un avoir
            </Button>
          ) : null}
          {brouillon && !confirmEmit ? (
            <Button
              disabled={f.chantier_id === null}
              onClick={() => {
                setConfirmEmit(true);
                setVuAttention(false);
              }}
            >
              <Lock className="h-4 w-4" /> Émettre la facture
            </Button>
          ) : null}
        </div>
      </div>

      {verdict.risque === 'depassement' ? (
        <div className="space-y-2 rounded-md border border-amber-400 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
          <p>
            <strong>
              Attention : cette facture dépasse de {formatEuro(verdict.ecartHt)} HT ce qu&apos;il
              reste à facturer sur ce chantier.
            </strong>
          </p>
          <table className="text-[12.5px]">
            <tbody>
              <tr>
                <td className="pr-4">Le chantier doit rapporter</td>
                <td className="text-right tabular-nums">{formatEuro(verdict.engageHt)} HT</td>
              </tr>
              <tr>
                <td className="pr-4">Déjà facturé dessus</td>
                <td className="text-right tabular-nums">
                  − {formatEuro(verdict.dejaFactureHt)} HT
                </td>
              </tr>
              <tr className="border-t font-medium">
                <td className="pr-4">Il reste donc à facturer</td>
                <td className="text-right tabular-nums">
                  {formatEuro(verdict.resteAFacturerHt)} HT
                </td>
              </tr>
              <tr>
                <td className="pr-4">Or ce brouillon facturerait</td>
                <td className="text-right tabular-nums">{formatEuro(verdict.brouillonHt)} HT</td>
              </tr>
            </tbody>
          </table>
          <p>
            Le plus souvent, c&apos;est un brouillon créé <em>avant</em> l&apos;acompte : il a gardé
            le montant plein. Supprimez-le et reconvertissez le devis — la facture de solde déduira
            l&apos;acompte toute seule.
          </p>
          <p className="text-[12.5px]">
            Rien n&apos;est bloqué : si ce montant est voulu — travaux supplémentaires, révision de
            prix — émettez sans crainte. Émise en <strong>acompte</strong>, elle ne facturerait
            qu&apos;une part de ce montant, et cet avertissement ne s&apos;applique pas.
          </p>
        </div>
      ) : null}

      {brouillon && f.chantier_id === null ? (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
          <strong>Émission bloquée :</strong> cette facture n&apos;est rattachée à aucun chantier.
          Choisissez-en un dans « Détails », juste en dessous — le bouton s&apos;activera aussitôt.
        </div>
      ) : null}

      {emit.isError ? (
        <div className="border-destructive/30 bg-destructive/10 text-destructive rounded-md border px-4 py-2 text-sm">
          Échec : {messageErreur(emit.error)}
        </div>
      ) : null}
      {avoirMut.isError ? (
        <div className="border-destructive/30 bg-destructive/10 text-destructive rounded-md border px-4 py-2 text-sm">
          Avoir refusé : {messageErreur(avoirMut.error)}
        </div>
      ) : null}

      {confirmAvoir ? (
        <div className="space-y-3 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm dark:border-amber-900 dark:bg-amber-950/40">
          <p className="text-amber-800 dark:text-amber-200">
            Un avoir sera émis pour <strong>{formatEuro(f.montant_ttc)} TTC</strong>, avec son
            propre numéro, et la facture <span className="font-mono">{f.numero}</span> passera en «
            Annulée (avoir) ». Opération définitive (NF525) : pour refacturer, repartir du devis.
          </p>
          {paye > 0 ? (
            <p className="font-medium text-amber-900 dark:text-amber-100">
              Attention : {formatEuro(paye)} déjà encaissés sur cette facture, à rembourser au
              client ou à imputer sur une autre facture.
            </p>
          ) : null}
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              disabled={avoirMut.isPending}
              onClick={() => {
                avoirMut.mutate();
              }}
            >
              {avoirMut.isPending ? 'Émission…' : 'Oui, émettre l’avoir'}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setConfirmAvoir(false);
              }}
            >
              Annuler
            </Button>
          </div>
        </div>
      ) : null}

      {brouillon && confirmEmit ? (
        <div className="space-y-3 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm dark:border-amber-900 dark:bg-amber-950/40">
          {/* Le récapitulatif : ce que l'émission va figer, et ce qui cloche. Une
              facture sur six finissait annulée par un avoir — c'est ici qu'on
              l'attrape, tant qu'une correction prend deux minutes. */}
          <div className="bg-card space-y-3 rounded-md border px-3 py-3">
            <p className="font-semibold">Récapitulatif — vérifiez, c&apos;est définitif</p>
            <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1">
              <dt className="text-muted-foreground">Client</dt>
              <dd>{contact ? contactDisplayName(contact) : '—'}</dd>
              <dt className="text-muted-foreground">Adresse figée</dt>
              <dd>
                {!controlesPrets ? (
                  '…'
                ) : adresseFigee ? (
                  [
                    adresseFigee.ligne1,
                    adresseFigee.ligne2,
                    `${adresseFigee.code_postal} ${adresseFigee.ville}`,
                  ]
                    .filter((x): x is string => x !== null && x.trim() !== '')
                    .join(', ')
                ) : (
                  <span className="text-destructive">aucune</span>
                )}
              </dd>
              <dt className="text-muted-foreground">Chantier</dt>
              <dd>
                {chantierFacture
                  ? `${chantierFacture.numero} — ${chantierFacture.libelle}`
                  : '—'}
              </dd>
              {f.objet ? (
                <>
                  <dt className="text-muted-foreground">Objet</dt>
                  <dd>{f.objet}</dd>
                </>
              ) : null}
              <dt className="text-muted-foreground">Montant</dt>
              <dd className="tabular-nums">
                {formatEuro(f.montant_ht)} HT
                {detailTva.map((t) => (
                  <span key={t.tauxCode}>
                    {' '}
                    · TVA {(t.tauxValeur * 100).toLocaleString('fr-FR')} %{' '}
                    {formatEuro(t.montantTva)}
                  </span>
                ))}{' '}
                · <strong>{formatEuro(f.montant_ttc)} TTC</strong>
              </dd>
              <dt className="text-muted-foreground">Lignes</dt>
              <dd>{f.factures_lignes.length}</dd>
              <dt className="text-muted-foreground">Échéance</dt>
              <dd>
                le {echeancePrevue} — {delaiJours} jours après l&apos;émission
              </dd>
            </dl>

            {adressesQ.isError || contactsQ.isError ? (
              <p className="text-destructive">
                Impossible de vérifier la facture : le message en haut de la page dit ce qui
                n&apos;a pas pu se charger. L&apos;émission attend.
              </p>
            ) : !controlesPrets ? (
              <p className="text-muted-foreground">Vérification…</p>
            ) : points.length === 0 ? (
              <p className="text-money-in flex items-center gap-2 font-medium">
                <CheckCheck className="h-4 w-4 shrink-0" />
                Rien à signaler.
              </p>
            ) : (
              <div className="space-y-2">
                <ul className="space-y-1.5">
                  {points.map((p) => (
                    <li
                      key={p.texte}
                      className={
                        p.niveau === 'bloquant'
                          ? 'text-destructive'
                          : 'text-amber-900 dark:text-amber-200'
                      }
                    >
                      <strong>{p.niveau === 'bloquant' ? 'Bloquant' : 'À vérifier'} :</strong>{' '}
                      {p.texte}
                    </li>
                  ))}
                </ul>
                <Link to={`/clients/${f.contact_id}`} className="text-primary text-sm hover:underline">
                  Ouvrir la fiche du client →
                </Link>
              </div>
            )}
          </div>

          <p className="text-amber-800 dark:text-amber-200">
            L&apos;émission attribue un numéro définitif et <strong>verrouille</strong> la facture
            (NF525, irréversible). Que faut-il facturer maintenant ?
          </p>

          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              variant={emissionAcompte ? 'outline' : 'default'}
              onClick={() => {
                setEmissionAcompte(false);
                setVuAttention(false);
              }}
            >
              La totalité — {formatEuro(f.montant_ttc)} TTC
            </Button>
            <Button
              type="button"
              size="sm"
              variant={emissionAcompte ? 'default' : 'outline'}
              onClick={() => {
                setEmissionAcompte(true);
                setVuAttention(false);
              }}
            >
              Un acompte
            </Button>
          </div>

          {emissionAcompte ? (
            <div className="space-y-3 rounded-md border bg-card px-3 py-3">
              <div className="flex flex-wrap items-end gap-3">
                <div className="space-y-1">
                  <Label htmlFor="acompte_pct">Pourcentage</Label>
                  <div className="flex items-center gap-1">
                    <Input
                      id="acompte_pct"
                      inputMode="decimal"
                      className="w-24"
                      value={valeurPct}
                      onChange={(e) => {
                        setSaisiePct(e.target.value);
                        setSaisieEur(null);
                        const p = nombre(e.target.value) / 100;
                        if (Number.isFinite(p)) setPart(p);
                      }}
                    />
                    <span className="text-muted-foreground">%</span>
                  </div>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="acompte_eur">Montant TTC</Label>
                  <div className="flex items-center gap-1">
                    <Input
                      id="acompte_eur"
                      inputMode="decimal"
                      className="w-32"
                      value={valeurEur}
                      onChange={(e) => {
                        setSaisieEur(e.target.value);
                        setSaisiePct(null);
                        const p = partDepuisMontant(nombre(e.target.value), totalTtc);
                        if (p !== null) setPart(p);
                      }}
                    />
                    <span className="text-muted-foreground">€</span>
                  </div>
                </div>
                <div className="flex gap-1">
                  {[0.3, 0.4, 0.5].map((v) => (
                    <Button
                      key={v}
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setPart(v);
                        setSaisiePct(null);
                        setSaisieEur(null);
                      }}
                    >
                      {Math.round(v * 100)} %
                    </Button>
                  ))}
                </div>
              </div>

              {refusAcompte === null ? (
                <div className="space-y-1">
                  <p>
                    Cette facture d&apos;acompte portera{' '}
                    <strong>{formatEuro(totauxAcomptePrevu.ttc)} TTC</strong> —{' '}
                    {formatEuro(totauxAcomptePrevu.ht)} HT et {formatEuro(totauxAcomptePrevu.tva)}{' '}
                    de TVA.
                  </p>
                  <p className="text-muted-foreground">
                    Le reste, {formatEuro(totalTtc - totauxAcomptePrevu.ttc)} TTC, se facturera
                    plus tard : depuis le devis, « Créer la facture » déduira cet acompte tout
                    seul.
                  </p>
                </div>
              ) : (
                <p className="text-destructive">{refusAcompte}</p>
              )}
            </div>
          ) : null}

          {controlesPrets && bloquants.length > 0 ? (
            <p className="text-destructive font-medium">
              Émission impossible tant que{' '}
              {bloquants.length > 1
                ? 'les points bloquants ne sont pas corrigés'
                : 'le point bloquant n’est pas corrigé'}
              .
            </p>
          ) : null}
          {controlesPrets && bloquants.length === 0 && attentions.length > 0 ? (
            <label className="flex items-start gap-2 font-medium">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4"
                checked={vuAttention}
                onChange={(e) => {
                  setVuAttention(e.target.checked);
                }}
              />
              <span>
                J&apos;ai vu {attentions.length > 1 ? 'ces points' : 'ce point'} : j&apos;émets
                quand même.
              </span>
            </label>
          ) : null}

          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              disabled={
                emit.isPending ||
                !emissionPossible ||
                (emissionAcompte && refusAcompte !== null)
              }
              onClick={() => {
                emit.mutate();
              }}
            >
              {emit.isPending
                ? 'Émission…'
                : emissionAcompte
                  ? `Oui, émettre l’acompte de ${formatEuro(totauxAcomptePrevu.ttc)}`
                  : 'Oui, émettre la facture entière'}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setConfirmEmit(false);
                setEmissionAcompte(false);
                setVuAttention(false);
              }}
            >
              Annuler
            </Button>
          </div>
        </div>
      ) : null}

      {!brouillon ? (
        <div className="bg-muted/40 text-muted-foreground flex items-center gap-2 rounded-md border px-4 py-2 text-sm">
          <Lock className="h-4 w-4" /> {avoir ? 'Avoir émis' : 'Facture émise'}
          {f.emise_at ? ` le ${formatDateFr(f.emise_at)}` : ''} — contenu verrouillé conformément à
          la loi anti-fraude TVA (NF525).
        </div>
      ) : null}

      {lie ? (
        <div className="rounded-md border px-4 py-2 text-sm">
          {avoir ? 'Annule la facture ' : 'Annulée par l’avoir '}
          <Link to={`/factures/${lie.id}`} className="text-primary font-mono hover:underline">
            {lie.numero}
          </Link>
          {` du ${formatDateFr(lie.date_emission)}`}
        </div>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Client</CardTitle>
          </CardHeader>
          <CardContent className="text-sm">
            <div className="font-medium">{contact ? contactDisplayName(contact) : '—'}</div>
            {contact?.email ? <div className="text-muted-foreground">{contact.email}</div> : null}
            {contact?.telephone ? (
              <div className="text-muted-foreground">{contact.telephone}</div>
            ) : null}
            {/* L'adresse qui fait foi : sur une facture émise, celle figée à
                l'émission — la fiche client a pu changer depuis, la facture
                non. Sur un brouillon, celle que l'émission figera. */}
            <div className="mt-3 border-t pt-3">
              <div className="text-muted-foreground text-xs">
                {brouillon
                  ? 'Adresse de facturation — sera figée à l’émission'
                  : 'Adresse figée à l’émission'}
              </div>
              {brouillon ? (
                adressesQ.isError ? (
                  <div className="text-muted-foreground">Illisible pour le moment.</div>
                ) : !adressesQ.isSuccess ? (
                  <div className="text-muted-foreground">…</div>
                ) : adresseFigee ? (
                  lignesAdresse(
                    adresseFigee.ligne1,
                    adresseFigee.ligne2,
                    adresseFigee.code_postal,
                    adresseFigee.ville,
                  ).map((l) => <div key={l}>{l}</div>)
                ) : (
                  <div className="text-destructive">
                    Aucune adresse sur la fiche client.{' '}
                    <Link to={`/clients/${f.contact_id}`} className="underline">
                      La compléter
                    </Link>
                  </div>
                )
              ) : f.facturation_adresse ? (
                lignesAdresse(
                  f.facturation_adresse.ligne1,
                  f.facturation_adresse.ligne2,
                  f.facturation_adresse.codePostal,
                  f.facturation_adresse.ville,
                ).map((l) => <div key={l}>{l}</div>)
              ) : (
                <div>
                  <div className="font-medium text-amber-700 dark:text-amber-400">
                    Aucune adresse n&apos;a été figée sur cette facture.
                  </div>
                  <div className="text-muted-foreground text-xs">
                    Son PDF affiche à la place l&apos;adresse actuelle de la fiche client, qui a pu
                    changer depuis l&apos;émission.
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Détails</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            <div>
              <span className="text-muted-foreground">Objet : </span>
              {f.objet}
            </div>
            <div>
              <span className="text-muted-foreground">Date d'émission : </span>
              {formatDateFr(f.date_emission)}
            </div>
            {avoir ? null : (
              <div>
                <span className="text-muted-foreground">Échéance : </span>
                {formatDateFr(f.date_echeance)}
              </div>
            )}
            {f.devis_id ? (
              <div>
                <span className="text-muted-foreground">Devis lié : </span>
                <Link to={`/devis/${f.devis_id}`} className="text-primary hover:underline">
                  voir le devis
                </Link>
              </div>
            ) : null}
            <div className="space-y-1 pt-1">
              <Label htmlFor="chantier_id">Chantier *</Label>
              <ChoixChantier
                id="chantier_id"
                chantiers={chantiersQ.data ?? []}
                clients={contactsQ.data ?? []}
                chargement={chantiersQ.isLoading}
                value={f.chantier_id ?? ''}
                invalide={f.chantier_id === null}
                onChange={(v) => {
                  chantierMut.mutate(v === '' ? null : v);
                }}
              />
              {f.chantier_id === null ? (
                <p className="text-destructive text-xs">
                  Sans chantier, le chiffre d&apos;affaires de cette facture ne compte nulle part.
                </p>
              ) : null}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-muted-foreground border-b text-left text-xs uppercase">
            <tr>
              <th className="px-4 py-3 font-medium">Désignation</th>
              <th className="px-4 py-3 text-right font-medium">Qté</th>
              <th className="px-4 py-3 font-medium">Unité</th>
              <th className="px-4 py-3 text-right font-medium">PU HT</th>
              <th className="px-4 py-3 text-right font-medium">Montant HT</th>
              <th className="px-4 py-3 font-medium">TVA</th>
            </tr>
          </thead>
          <tbody>
            {f.factures_lignes.map((l) => {
              const tva = l.taux_tva_id ? tvaMap.get(l.taux_tva_id) : undefined;
              return (
                <tr key={l.id} className="border-b align-top last:border-0">
                  <td className="px-4 py-2">
                    {l.libelle}
                    {l.description === null || l.description.trim() === '' ? null : (
                      <div className="text-muted-foreground mt-0.5 whitespace-pre-line text-xs">
                        {l.description}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {Number(l.quantite).toLocaleString('fr-FR')}
                  </td>
                  <td className="text-muted-foreground px-4 py-2">
                    {l.unite_id ? (uniteMap.get(l.unite_id)?.code ?? '') : ''}
                  </td>
                  <td className="px-4 py-2 text-right">{formatEuro(l.prix_unitaire_ht)}</td>
                  <td className="px-4 py-2 text-right">{formatEuro(l.total_ht)}</td>
                  <td className="text-muted-foreground px-4 py-2">
                    {tva ? formatTauxPct(tva.taux) : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      <div className="ml-auto max-w-xs space-y-1 text-sm">
        <div className="flex justify-between">
          <span className="text-muted-foreground">Total HT</span>
          <span className="font-medium">{formatEuro(f.montant_ht)}</span>
        </div>
        {detailTva.map((t) => (
          <div key={t.tauxCode} className="text-muted-foreground flex justify-between text-xs">
            <span>TVA {(t.tauxValeur * 100).toLocaleString('fr-FR')} %</span>
            <span>{formatEuro(t.montantTva)}</span>
          </div>
        ))}
        <div className="flex justify-between border-t pt-1 text-base">
          <span className="font-semibold">Total TTC</span>
          <span className="font-bold">{formatEuro(f.montant_ttc)}</span>
        </div>
      </div>

      {!brouillon ? (
        <Card>
          <CardHeader>
            <CardTitle>{avoir ? 'Remboursement' : 'Règlements'}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm">
            {(reglementsQ.data ?? []).length > 0 ? (
              <table className="w-full">
                <thead className="text-muted-foreground text-left text-xs uppercase">
                  <tr>
                    <th className="py-1 font-medium">Date</th>
                    <th className="font-medium">Mode</th>
                    <th className="font-medium">Référence</th>
                    <th className="text-right font-medium">Montant</th>
                    <th className="w-8" />
                  </tr>
                </thead>
                <tbody>
                  {(reglementsQ.data ?? []).map((r) => (
                    <tr key={r.id} className="border-t">
                      <td className="py-1.5">{r.date_reglement}</td>
                      <td>{REGLEMENT_MODE_LABELS[r.mode] ?? r.mode}</td>
                      <td className="text-muted-foreground">{r.reference ?? '—'}</td>
                      <td
                        className={`text-right font-medium ${
                          Number(r.montant) === 0 ? 'text-destructive' : ''
                        }`}
                        title={
                          Number(r.montant) === 0
                            ? 'Un règlement de 0 € : saisie perdue, à supprimer.'
                            : undefined
                        }
                      >
                        {formatEuro(r.montant)}
                      </td>
                      <td className="text-right">
                        {regSuppr === r.id ? (
                          <span className="flex items-center justify-end gap-1">
                            <button
                              type="button"
                              className="text-destructive text-[11px] underline"
                              disabled={supprReglementMut.isPending}
                              onClick={() => {
                                supprReglementMut.mutate(r.id);
                              }}
                            >
                              confirmer
                            </button>
                            <button
                              type="button"
                              className="text-muted-foreground text-[11px] underline"
                              onClick={() => {
                                setRegSuppr(null);
                              }}
                            >
                              non
                            </button>
                          </span>
                        ) : (
                          <button
                            type="button"
                            className="text-muted-foreground hover:text-destructive"
                            title="Supprimer ce règlement"
                            onClick={() => {
                              setRegSuppr(r.id);
                            }}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-muted-foreground">Aucun règlement enregistré.</p>
            )}

            {annulee ? (
              <div className="text-muted-foreground border-t pt-2">
                Facture annulée par un avoir : plus rien à encaisser.
              </div>
            ) : (
              <div className="flex justify-between border-t pt-2 font-medium">
                <span>{avoir ? 'Reste à rembourser' : 'Reste à payer'}</span>
                <span className={reste <= 0 ? 'text-green-600' : undefined}>
                  {formatEuro(reste)}
                </span>
              </div>
            )}

            {f.statut !== 'payee' && !annulee ? (
              <div className="grid gap-2 sm:grid-cols-5 sm:items-end">
                <div className="space-y-1">
                  <Label htmlFor="reg_mode">Mode</Label>
                  <Select
                    id="reg_mode"
                    value={regMode}
                    onChange={(e) => {
                      setRegMode(e.target.value);
                    }}
                  >
                    {Object.entries(REGLEMENT_MODE_LABELS).map(([v, l]) => (
                      <option key={v} value={v}>
                        {l}
                      </option>
                    ))}
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="reg_montant">Montant (€)</Label>
                  <Input
                    id="reg_montant"
                    inputMode="decimal"
                    placeholder="2360,16"
                    value={regMontant}
                    aria-invalid={erreurMontant !== null}
                    onChange={(e) => {
                      setRegMontant(e.target.value);
                    }}
                  />
                  {/* Ce que le CRM a COMPRIS, sous le champ. Une saisie mal lue
                      se voyait autrefois seulement une fois enregistrée — à
                      0,00 €, et sans moyen de revenir en arrière. */}
                  {erreurMontant !== null ? (
                    <p className="text-destructive text-[11px]">{erreurMontant}</p>
                  ) : montantLu !== null ? (
                    <p className="text-muted-foreground text-[11px]">
                      Sera enregistré : <strong>{formatEuro(montantLu)}</strong>
                    </p>
                  ) : reste > 0 ? (
                    <button
                      type="button"
                      className="text-primary text-[11px] underline"
                      onClick={() => {
                        setRegMontant(reste.toFixed(2).replace('.', ','));
                      }}
                    >
                      solde : {formatEuro(reste)}
                    </button>
                  ) : null}
                </div>
                <div className="space-y-1">
                  <Label htmlFor="reg_date">Date</Label>
                  <Input
                    id="reg_date"
                    type="date"
                    value={regDate}
                    onChange={(e) => {
                      setRegDate(e.target.value);
                    }}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="reg_ref">Référence</Label>
                  <Input
                    id="reg_ref"
                    value={regRef}
                    onChange={(e) => {
                      setRegRef(e.target.value);
                    }}
                  />
                </div>
                <Button
                  type="button"
                  disabled={reglementMut.isPending || montantLu === null}
                  onClick={() => {
                    reglementMut.mutate();
                  }}
                >
                  {avoir ? 'Enregistrer le remboursement' : 'Enregistrer'}
                </Button>
              </div>
            ) : null}
            {reglementMut.isError ? (
              <p className="text-destructive">{messageErreur(reglementMut.error)}</p>
            ) : null}
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
